// src/components/ACStatus.jsx
import React, { useState, useEffect } from 'react';
import '../styles.css';

const ACStatus = () => {
  const [ac1Status, setAc1Status] = useState(false);
  const [ac2Status, setAc2Status] = useState(false);
  const [ac1LdrValue, setAc1LdrValue] = useState(0);
  const [ac2LdrValue, setAc2LdrValue] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [lastUpdate, setLastUpdate] = useState(null);

  const CHANNEL_ID = '3018449';
  const READ_API_KEY = 'WPIXJNON8E0WC5U0';

  const getThingSpeakUrl = () => {
    const baseUrl = `https://api.thingspeak.com/channels/${CHANNEL_ID}/feeds/last.json`;
    return READ_API_KEY ? `${baseUrl}?api_key=${READ_API_KEY}` : baseUrl;
  };

  const LDR_THRESHOLD = 2000;
  const OFFLINE_THRESHOLD_MINUTES = 5;

  useEffect(() => {
    const fetchLDRData = async () => {
      try {
        setConnectionStatus('Fetching...');
        const response = await fetch(getThingSpeakUrl());
        if (response.ok) {
          const data = await response.json();

          // Check if created_at exists and is recent
          if (!data.created_at) {
            setConnectionStatus('Device Offline');
            setAc1Status(false);
            setAc2Status(false);
            setAc1LdrValue(0);
            setAc2LdrValue(0);
            setLastUpdate(null);
            return;
          }

          const lastUpdateTime = new Date(data.created_at);
          const now = new Date();
          const diffMinutes = (now - lastUpdateTime) / 1000 / 60; // minutes difference

          if (diffMinutes > OFFLINE_THRESHOLD_MINUTES) {
            setConnectionStatus('Device Offline');
            setAc1Status(false);
            setAc2Status(false);
            setAc1LdrValue(0);
            setAc2LdrValue(0);
            setLastUpdate(null);
            return;
          }

          // Data is recent — update states
          const ldr1 = parseInt(data.field6);
          const ldr2 = parseInt(data.field7);

          if (!isNaN(ldr1)) {
            setAc1LdrValue(ldr1);
            setAc1Status(ldr1 > LDR_THRESHOLD);
          }

          if (!isNaN(ldr2)) {
            setAc2LdrValue(ldr2);
            setAc2Status(ldr2 > LDR_THRESHOLD);
          }

          setConnectionStatus('Connected');
          setLastUpdate(lastUpdateTime.toLocaleTimeString());
        } else {
          setConnectionStatus('Error Fetching Data');
        }
      } catch (error) {
        console.error('ThingSpeak fetch error:', error);
        setConnectionStatus('Error');
      }
    };

    fetchLDRData();
    const interval = setInterval(fetchLDRData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (isOn) => (isOn ? '#4CAF50' : '#f44336');

  const renderStatus = (isOn, ldrValue, unitName) => {
    return (
      <div
        className="status-card"
        style={{ borderLeft: `4px solid ${getStatusColor(isOn)}` }}
      >
        <div className="status-content">
          <div className="status-info">
            <span className="status-icon">❄️</span>
            <p style={{fontSize:'16px'}}>
              <strong>Status:</strong> {isOn ? 'ON' : 'OFF'}
            </p>
            <p style={{fontSize:'16px'}}>
              <strong>LDR Value:</strong> {ldrValue}
            </p>
          </div>
          <span className="status-indicator" style={{ marginLeft: 'auto' }}>
            {isOn ? '🟢' : '🔴'}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="main-content">
      <div className="ac-container">
        <div className="header-section">
          <h2>AC Status Monitor 🧊</h2>
          <div
            className="connection-status"
            style={{
              color: connectionStatus === 'Connected' ? '#22cf27ff' : '#f44336',
              fontSize: '14px',
              marginBottom: '10px',
            }}
          >
            <strong>ThingSpeak Status:</strong> {connectionStatus}
            {lastUpdate && (
              <span style={{ marginLeft: '15px' }}>Last Update: {lastUpdate}</span>
            )}
          </div>
        </div>

        {connectionStatus === 'Device Offline' ? (
          <div
            style={{
              color: '#f44336',
              fontWeight: 'bold',
              textAlign: 'center',
              padding: '20px',
              fontSize: '16px',
            }}
          >
            ⚠️ Device Offline or No Recent Data
          </div>
        ) : (
          <div className="ac-units">
            <div className="ac-unit">
              <h3>AC Unit 1</h3>
              {renderStatus(ac1Status, ac1LdrValue, 'Living Room')}
            </div>

            <div className="ac-unit">
              <h3>AC Unit 2</h3>
              {renderStatus(ac2Status, ac2LdrValue, 'Bedroom')}
            </div>
          </div>
        )}

        <div className="info-section">
          <h4 style={{ color: 'black' }}>Status Legend</h4>
          <div className="ranges">
            <div className="range-item">
              <span className="range-icon">🟢</span>
              <p>ON - LDR Value &gt; {LDR_THRESHOLD} (AC LED indicator bright)</p>
            </div>
            <div className="range-item">
              <span className="range-icon">🔴</span>
              <p>OFF - LDR Value ≤ {LDR_THRESHOLD} (AC LED indicator dim/off)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ACStatus;
